/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { Button, Center, Loader, Text, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconDownload, IconEye, IconTrash, IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { TransactionType, TransactionTypesCriteria } from '@e-origin/shared';

import {
  getInitialTransactionTypeCriteria,
  useAdminExportTransactionType,
  useDeleteTransactionType,
  useTransactionTypeCriteriaUrlSync,
  useTransactionTypes,
} from '@admin/hooks';
import { SearchBox, Table, TableActionEnum, TableActions } from '@components';
import { TABLE_MENU_THRESHOLD } from '@constants';
import { ContentLayout } from '@features/layout';
import { EditTransactionType } from './EditTransactionType';
import { ImportTransactionType } from './ImportTransactionType';

export const ManageTransactionTypes = () => {
  const theme = useMantineTheme();

  const [searchParams] = useSearchParams();
  const initialCriteria = getInitialTransactionTypeCriteria(searchParams);

  const [filters, setFilters] = useState<TransactionTypesCriteria['filters']>(initialCriteria.filters);
  const [sorting, setSorting] = useState<TransactionTypesCriteria['sorting']>(initialCriteria.sorting);
  const [pagination, setPagination] = useState<TransactionTypesCriteria['pagination']>(initialCriteria.pagination);
  useTransactionTypeCriteriaUrlSync({ filters, sorting, pagination });
  const { data: { transactionTypes = [], totalItems = 0 } = {}, isLoading } = useTransactionTypes({
    filters,
    sorting,
    pagination,
  });
  const deleteTransactionTypeMutation = useDeleteTransactionType();
  const exportTransactionTypeMutation = useAdminExportTransactionType();

  const searchTransactionType = (searchQuery: string) => {
    setFilters({
      ...filters,
      searchQuery: searchQuery,
    });
    setPagination({ ...pagination, page: 1 });
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  const openImportTransactionTypeModal = () => {
    modals.open({
      title: 'Import Transaction Type',
      children: <ImportTransactionType onClose={() => modals.closeAll()} />,
      size: 'lg',
      centered: true,
    });
  };

  const openTransactionTypeModal = (transactionType?: TransactionType) => {
    modals.open({
      title: transactionType ? 'Edit Transaction Type' : 'New Transaction Type',
      children: <EditTransactionType transactionType={transactionType} onHide={() => modals.closeAll()} />,
      size: 'lg',
    });
  };

  const handleTableAction = async (actionType: string, row?: TransactionType) => {
    switch (actionType) {
      case TableActionEnum.Create: {
        openTransactionTypeModal();
        break;
      }
      case TableActionEnum.Edit: {
        openTransactionTypeModal(row);
        break;
      }
      case TableActionEnum.Delete: {
        modals.openConfirmModal({
          title: 'Confirm transaction type delete',
          children: (
            <Text>
              Are you sure you want to delete the transaction type? <br />
              This action cannot be undone!
            </Text>
          ),
          labels: { confirm: 'Delete', cancel: 'Cancel' },
          confirmProps: { color: 'red' },
          onConfirm: async () => {
            await deleteTransactionTypeMutation.mutateAsync(row._id);
            modals.closeAll();
          },
        });
        break;
      }
      default:
        break;
    }
  };

  const columns = [
    {
      name: 'Transaction type name',
      selector: 'name',
      cell: (row) => row.name,
      sortable: true,
    },
    {
      name: 'Type id',
      selector: 'typeId',
      cell: (row) => row.typeId,
      sortable: true,
    },
    {
      name: 'Max value',
      selector: 'maxValue',
      cell: (row) => row.maxValue,
      sortable: true,
    },
    {
      name: 'Actions',
      sticky: 'right' as any,
      cell: (row, rowIdx) => (
        <TableActions
          actions={[
            {
              iconComponent: <IconEye color={theme.colors[theme.primaryColor][6]} />,
              text: 'Edit',
              onClick: () => handleTableAction(TableActionEnum.Edit, row),
            },
            {
              iconComponent: <IconTrash color={theme.colors[theme.primaryColor][6]} />,
              text: 'Delete',
              onClick: () => handleTableAction(TableActionEnum.Delete, row),
            },
            {
              iconComponent: <IconDownload color={theme.colors[theme.primaryColor][6]} />,
              text: 'Export transaction type',
              onClick: () => exportTransactionTypeMutation.mutate(row._id),
            },
          ]}
          disableAfterClick={false}
          moveMenuUpwards={pagination.size - rowIdx <= TABLE_MENU_THRESHOLD}
          data-pw={rowIdx}
        />
      ),
    },
  ];

  if (isLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Manage Transaction Types</ContentLayout.Title>
        <ContentLayout.Filters>
          <Button leftSection={<IconUpload size={16} />} onClick={openImportTransactionTypeModal}>
            Import Transaction Type
          </Button>

          <Button
            data-pw="new-transaction-type-btn"
            onClick={() => {
              handleTableAction(TableActionEnum.Create);
            }}
            variant="outline"
            size="md"
            h={55}
          >
            New transaction type
          </Button>

          <SearchBox handleSearch={searchTransactionType} value={filters?.searchQuery} placeholder="Seach by name..." data-pw="transaction-type-search-input" />
        </ContentLayout.Filters>
      </ContentLayout.Header>

      <ContentLayout.Content>
        <Table
          columns={columns}
          dataSource={transactionTypes || []}
          total={totalItems}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          isLoading={isLoading}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
